"use client";

import { useEffect, useState, useRef } from "react";
import { useSession } from "next-auth/react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useMessaging } from "@/contexts/messaging-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Minimize2,
  ChevronsUpDown,
  X,
  Send,
  MessageSquare,
  ChevronUp,
  MoreVertical,
  Info
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { sendMessage, markConversationAsRead } from "@/app/actions/messaging";
import { getPusherClient } from "@/lib/pusher-client";
import { toast } from "sonner";

interface Message {
  id: string;
  content: string;
  createdAt: string;
  isRead: boolean;
  sender: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
}

interface WidgetConversation {
  id: string;
  otherUser: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
  listing?: {
    id: string;
    title: string;
    address: string;
    price: number;
    images: string[];
  } | null;
  lastMessagePreview: string | null;
  unreadCount: number;
}

export function MessageChatWidget() {
  const { data: session } = useSession();

  const {
    isWidgetOpen,
    isWidgetMinimized,
    activeConversationId,
    closeWidget,
    toggleWidgetMinimize,
    openModal,
    conversations
  } = useMessaging();

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<WidgetConversation | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Fetch conversation details
  useEffect(() => {
    if (activeConversationId && isWidgetOpen) {
      fetchConversation();
      fetchMessages();
      markAsRead();

      // Subscribe to real-time updates
      const pusher = getPusherClient();
      const channel = pusher.subscribe(`conversation-${activeConversationId}`);

      channel.bind("new-message", (data: { message: Message }) => {
        setMessages((prev) => [...prev, data.message]);
        markAsRead();
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [activeConversationId, isWidgetOpen]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (lastMessageRef.current && !isWidgetMinimized) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isWidgetMinimized]);

  async function fetchConversation() {
    if (!activeConversationId) return;

    try {
      const response = await fetch(`/api/conversations/${activeConversationId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(data);
      }
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
    }
  }

  async function fetchMessages() {
    if (!activeConversationId) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/conversations/${activeConversationId}/messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  }

  async function markAsRead() {
    if (activeConversationId) {
      await markConversationAsRead(activeConversationId);
    }
  }

  async function handleSend() {
    if (!inputValue.trim() || sending || !activeConversationId) return;

    const content = inputValue.trim();
    setInputValue("");
    setSending(true);

    try {
      const result = await sendMessage(activeConversationId, content);

      if (result.error) {
        toast.error(result.error);
        setInputValue(content);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
      setInputValue(content);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!isWidgetOpen || !activeConversationId) return null;

  // Get most recent conversation from the list or use the selected one
  const activeConv = conversations.find(c => c.id === activeConversationId) || currentConversation;

  return (
    <div
      data-tour="messaging-widget"
      className={cn(
        "fixed bottom-4 right-4 z-[var(--z-modal)]",
        "w-[380px] max-w-[calc(100vw-2rem)]",
        isWidgetMinimized ? "h-14" : "h-[500px]",
        "sm:bottom-6 sm:right-6",
        "transition-[height,transform,opacity] duration-300 ease-out",
        "animate-slide-in-bottom"
      )}>
      {/* Glass-morphism container with enhanced aesthetic */}
      <div className={cn(
        "h-full rounded-lg",
        "bg-gray-900/90 backdrop-blur-xl",
        "border border-cyan-500/20",
        "shadow-[0_0_30px_rgba(6,182,212,0.15),0_8px_32px_rgba(0,0,0,0.4)]",
        "ring-1 ring-cyan-500/10",
        "transition-[border-color,box-shadow] duration-300 ease-out",
        "hover:border-cyan-500/30 hover:shadow-[0_0_40px_rgba(6,182,212,0.25),0_8px_32px_rgba(0,0,0,0.4)]"
      )}>
        {/* Header with gradient and glow */}
        <div className={cn(
          "flex items-center justify-between px-4 py-3",
          "border-b border-cyan-500/20",
          "bg-gradient-to-r from-gray-900/80 via-gray-900/60 to-gray-800/80",
          "relative overflow-hidden"
        )}>
          {/* Subtle top glow line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

          <div className="flex items-center gap-3 flex-1 min-w-0">
            {activeConv ? (
              <>
                <Avatar className="h-8 w-8 border-2 border-cyan-500/30 ring-2 ring-cyan-500/10">
                  <AvatarImage src={activeConv.otherUser.image || undefined} />
                  <AvatarFallback className="bg-gray-800 text-cyan-400">
                    {activeConv.otherUser.name?.[0] || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white truncate drop-shadow-sm">
                    {activeConv.otherUser.name || "User"}
                  </h3>
                  {activeConv.listing && (
                    <p className="text-xs text-cyan-400/90 truncate">
                      {activeConv.listing.title}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
                <span className="text-sm font-semibold text-white drop-shadow-sm">Messages</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Info Button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    "hover:bg-cyan-500/10 hover:text-cyan-400",
                    "transition-[background-color,color,transform] duration-200 ease-out",
                    "hover:scale-110"
                  )}
                >
                  <Info className="h-4 w-4 text-gray-400 transition-colors duration-200" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="left"
                className="w-80 p-4 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/20 border-cyan-200 dark:border-cyan-800"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    <h4 className="font-semibold text-cyan-900 dark:text-cyan-100">Real-Time Messaging</h4>
                  </div>
                  <p className="text-sm text-cyan-800 dark:text-cyan-200 leading-relaxed">
                    Connect directly with property owners, brokers, and other users instantly!
                  </p>
                  <div className="bg-card/60 dark:bg-black/20 rounded-md p-3 space-y-2">
                    <p className="text-xs font-semibold text-cyan-900 dark:text-cyan-100">Features:</p>
                    <ul className="text-xs text-cyan-800 dark:text-cyan-200 space-y-1 list-disc list-inside">
                      <li><strong>Instant delivery</strong> - Messages appear in real-time</li>
                      <li><strong>Email notifications</strong> - Get notified even when offline</li>
                      <li><strong>Property context</strong> - Messages linked to specific listings</li>
                      <li><strong>Conversation history</strong> - All your chats saved</li>
                    </ul>
                  </div>
                  <p className="text-xs text-cyan-700 dark:text-cyan-300">
                    💡 <strong>Tip:</strong> Click the maximize button to open full messaging view, or minimize to keep chatting while browsing!
                  </p>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                "hover:bg-cyan-500/10 hover:text-cyan-400",
                "transition-[background-color,color,transform] duration-200 ease-out",
                "hover:scale-110"
              )}
              onClick={() => openModal(activeConversationId)}
            >
              <ChevronsUpDown className="h-4 w-4 text-gray-400 transition-colors duration-200" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                "hover:bg-cyan-500/10 hover:text-cyan-400",
                "transition-[background-color,color,transform] duration-200 ease-out",
                "hover:scale-110"
              )}
              onClick={toggleWidgetMinimize}
            >
              {isWidgetMinimized ? (
                <ChevronUp className="h-4 w-4 text-gray-400 transition-colors duration-200" />
              ) : (
                <Minimize2 className="h-4 w-4 text-gray-400 transition-colors duration-200" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8",
                "hover:bg-red-500/10 hover:text-red-400",
                "transition-[background-color,color,transform] duration-200 ease-out",
                "hover:scale-110"
              )}
              onClick={closeWidget}
            >
              <X className="h-4 w-4 text-gray-400 transition-colors duration-200" />
            </Button>
          </div>
        </div>

        {/* Content - Hidden when minimized */}
        {!isWidgetMinimized && (
          <>
            {/* Messages Area with custom scrollbar */}
            <div
              ref={scrollAreaRef}
              className={cn(
                "flex-1 overflow-y-auto px-4 py-4 h-[340px]",
                "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-cyan-500/20",
                "hover:scrollbar-thumb-cyan-500/30"
              )}
            >
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm text-gray-400 animate-pulse">Loading messages...</div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <MessageSquare className="h-10 w-10 mx-auto text-gray-600 mb-3 opacity-50" />
                    <p className="text-sm text-gray-400 font-medium">No messages yet</p>
                    <p className="text-xs text-gray-500 mt-1.5">Start the conversation!</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const isOwnMessage = message.sender.id === session?.user?.id;
                    const isLastMessage = index === messages.length - 1;

                    return (
                      <div
                        key={message.id}
                        ref={isLastMessage ? lastMessageRef : null}
                        className={cn(
                          "flex gap-2.5 animate-fade-in",
                          isOwnMessage ? "flex-row-reverse" : ""
                        )}
                      >
                        <Avatar className={cn(
                          "h-8 w-8 shrink-0 border-2 ring-1",
                          isOwnMessage
                            ? "border-cyan-500/30 ring-cyan-500/10"
                            : "border-border/10 ring-white/5"
                        )}>
                          <AvatarImage src={message.sender.image || undefined} />
                          <AvatarFallback className={cn(
                            "text-xs",
                            isOwnMessage ? "bg-gray-800 text-cyan-400" : "bg-gray-800 text-gray-400"
                          )}>
                            {message.sender.name?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>

                        <div
                          className={cn(
                            "flex flex-col gap-1 max-w-[75%]",
                            isOwnMessage ? "items-end" : ""
                          )}
                        >
                          <div
                            className={cn(
                              "rounded-xl px-3.5 py-2.5 text-sm",
                              "backdrop-blur-sm",
                              "transition-[transform,box-shadow] duration-200 ease-out",
                              "hover:scale-[1.02]",
                              isOwnMessage
                                ? "bg-cyan-500/30 text-cyan-50 border border-cyan-400/40 shadow-lg shadow-cyan-500/20"
                                : "bg-gray-800/60 text-gray-200 border border-border/10 shadow-md shadow-black/20"
                            )}
                          >
                            <p className="whitespace-pre-wrap break-words leading-relaxed">
                              {message.content}
                            </p>
                          </div>
                          <span className="text-[10px] text-gray-500 px-1.5">
                            {formatDistanceToNow(new Date(message.createdAt), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Input Area with enhanced styling */}
            <div className="border-t border-cyan-500/20 bg-gray-900/60 p-3.5">
              <div className="flex gap-2.5">
                <Textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className={cn(
                    "flex-1 min-h-[60px] max-h-[80px] resize-none",
                    "bg-gray-800/60 border-border/15",
                    "focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20",
                    "placeholder:text-gray-500 text-gray-200",
                    "transition-[border-color,box-shadow] duration-200",
                    "rounded-lg"
                  )}
                  disabled={sending}
                />
                <Button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || sending}
                  size="icon"
                  className={cn(
                    "h-[60px] w-[60px] shrink-0",
                    "bg-cyan-500/25 hover:bg-cyan-500/35",
                    "border-2 border-cyan-500/40",
                    "shadow-lg shadow-cyan-500/20",
                    "disabled:opacity-40 disabled:cursor-not-allowed",
                    "transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out",
                    "hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/30",
                    !inputValue.trim() && "opacity-50",
                    sending && "animate-pulse"
                  )}
                >
                  <Send className={cn(
                    "h-4 w-4 text-cyan-400",
                    "transition-transform duration-200",
                    "group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  )} />
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 px-1.5">
                Press Enter to send • Shift+Enter for new line
              </p>
            </div>
          </>
        )}

        {/* Minimized State Badge with pulse animation */}
        {isWidgetMinimized && activeConv && activeConv.unreadCount > 0 && (
          <Badge
            variant="destructive"
            className={cn(
              "absolute -top-2 -right-2 h-5 min-w-[20px] rounded-full px-1.5 text-xs font-bold",
              "bg-cyan-500 hover:bg-cyan-500",
              "border-2 border-gray-900",
              "shadow-lg shadow-cyan-500/50",
              "animate-pulse-subtle"
            )}
          >
            {activeConv.unreadCount}
          </Badge>
        )}
      </div>
    </div>
  );
}